import { PERMISSIONS } from '@tempo-io/permissions';
import { defineNotificationCategory } from './types';

// A brand-new Loop has no existing watchers yet, and this is a one-time broadcast to whoever
// manages Loops org-wide rather than something tied to a Loop someone could already be watching.
export const portfolioCreatedDescriptor = defineNotificationCategory('portfolio_created', {
  actionUrl: (data, organizationId) => `/org-id/${organizationId}/loop/${data.loopId}`,
  pills: () => ['Portfolio'],
  audience: PERMISSIONS.EDIT_LOOP,
  scope: 'account',
  defaultChannels: ['in_app', 'slack'],
});

export const teamAssignmentChangedDescriptor = defineNotificationCategory(
  'team_assignment_changed',
  {
    actionUrl: (data, organizationId) => `/org-id/${organizationId}/loop/${data.loopId}`,
    pills: () => ['Team'],
    audience: PERMISSIONS.EDIT_CAPACITY,
    scope: 'entity',
    defaultChannels: ['in_app'],
  },
);

export const planPublishedDescriptor = defineNotificationCategory('plan_published', {
  actionUrl: (data, organizationId) => `/org-id/${organizationId}/loop/${data.loopId}`,
  pills: () => ['Portfolio'],
  audience: PERMISSIONS.EDIT_PLANNER,
  scope: 'entity',
  defaultChannels: ['email', 'in_app', 'slack'],
});

// Deliberately not `nonDisableable` — that list is a product designation (see
// registry-invariants.test.ts), so this stays togglable like any other planner notification.
export const scenarioOperationFailedDescriptor = defineNotificationCategory(
  'scenario_operation_failed',
  {
    actionUrl: (data, organizationId) =>
      `/org-id/${organizationId}/loop/${data.loopId}/planner/scenario/${data.scenarioId}`,
    audience: PERMISSIONS.EDIT_PLANNER,
    scope: 'entity',
    defaultChannels: ['in_app'],
  },
);

const objectiveJumpUrl = (
  data: { objectiveId: number; loopId?: number | null },
  organizationId: string,
) => {
  const path =
    data.loopId != null
      ? `/org-id/${organizationId}/loop/${data.loopId}/strategy`
      : `/org-id/${organizationId}/objectives`;
  return `${path}?jumpToObjectiveId=${data.objectiveId}`;
};

export const objectiveHealthChangedDescriptor = defineNotificationCategory(
  'objective_health_changed',
  {
    actionUrl: objectiveJumpUrl,
    pills: () => ['OKR'],
    audience: PERMISSIONS.EDIT_OBJECTIVES,
    scope: 'entity',
    defaultChannels: ['in_app'],
  },
);

export const objectiveDriftDetectedDescriptor = defineNotificationCategory(
  'objective_drift_detected',
  {
    actionUrl: objectiveJumpUrl,
    nonDisableable: true,
    pills: () => ['OKR'],
    audience: PERMISSIONS.EDIT_OBJECTIVES,
    scope: 'entity',
    defaultChannels: ['email', 'in_app'],
  },
);

const PORTFOLIO_URL_SEGMENT = {
  swimlane: 'swimlane',
  timeline: 'roadmap',
  table: 'table',
} as const;

// The result of the viewer's own export request, not something anyone else would watch.
export const exportCompletionDescriptor = defineNotificationCategory('export_completion', {
  actionUrl: (data, organizationId) =>
    data.target === 'dashboard'
      ? `/org-id/${organizationId}/dashboards/${data.dashboardId}`
      : `/org-id/${organizationId}/loop/${data.loopId}/portfolio/view/${data.viewId}/${PORTFOLIO_URL_SEGMENT[data.visualizationType]}`,
  pills: (_data, ctx) => (ctx.loopName ? [ctx.loopName, 'Export'] : ['Export']),
  audience: PERMISSIONS.VIEW_LOOP_VISUALIZATION,
  scope: 'account',
  defaultChannels: ['in_app'],
});

// The result of the viewer's own generation request, not something anyone else would watch.
export const aiDashboardGenerationCompletedDescriptor = defineNotificationCategory(
  'ai_dashboard_generation_completed',
  {
    actionUrl: (data, organizationId) =>
      data.dashboardId
        ? `/org-id/${organizationId}/dashboards/${data.dashboardId}`
        : `/org-id/${organizationId}/dashboards`,
    pills: () => ['AI Dashboard'],
    audience: PERMISSIONS.VIEW_INSIGHTS,
    scope: 'account',
    defaultChannels: ['in_app'],
  },
);
